import { CommonModule } from '@angular/common';
import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    Output,
    ViewChild,
    AfterViewInit,
    HostListener,
} from '@angular/core';

@Component({
    selector: 'lib-pace-signature',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './pace-signature.component.html',
    styleUrl: './pace-signature.component.css',
})
export class PaceSignatureComponent implements AfterViewInit {
    @ViewChild('signatureCanvas')
    public canvasRef!: ElementRef<HTMLCanvasElement>;

    @Input()
    public backgroundColor = '#fff';

    @Input()
    public strokeColor = '#444';

    @Input()
    public showClear = true;

    @Input()
    public clearLabel = 'clear';

    @Output()
    public signatureData = new EventEmitter<string>();

    private ctx: CanvasRenderingContext2D | null = null;
    private drawing = false;
    private lastX = 0;
    private lastY = 0;
    private lastTime = 0;
    private minThickness = 1;
    private maxThickness = 4;

    public ngAfterViewInit(): void {
        const canvas = this.canvasRef.nativeElement;
        this.ctx = canvas.getContext('2d');
        this.resizeCanvas();
    }

    @HostListener('window:resize')
    public resizeCanvas(): void {
        const canvas = this.canvasRef.nativeElement;
        const tempImage = new Image();
        tempImage.src = canvas.toDataURL();
        tempImage.onload = (): void => {
            canvas.width = canvas.clientWidth;
            canvas.height = canvas.clientHeight;
            this.clearCanvas();
            this.ctx?.drawImage(tempImage, 0, 0);
        };
    }

    private startDrawing(event: MouseEvent | TouchEvent): void {
        this.drawing = true;
        const { x, y } = this.getPosition(event);
        this.lastX = x;
        this.lastY = y;
        this.lastTime = Date.now();
    }

    private draw(event: MouseEvent | TouchEvent): void {
        if (!this.drawing || !this.ctx) return;

        const { x, y } = this.getPosition(event);
        const currentTime = Date.now();
        const timeDelta = currentTime - this.lastTime;
        const distance = Math.sqrt((x - this.lastX) ** 2 + (y - this.lastY) ** 2);
        const velocity = distance / timeDelta;

        this.ctx.lineWidth = Math.max(this.minThickness, Math.min(this.maxThickness, this.maxThickness - velocity * 2));
        this.ctx.lineCap = 'round';
        this.ctx.strokeStyle = this.strokeColor;
        this.ctx.beginPath();
        this.ctx.moveTo(this.lastX, this.lastY);
        this.ctx.lineTo(x, y);
        this.ctx.stroke();

        this.lastX = x;
        this.lastY = y;
        this.lastTime = currentTime;
    }

    public stopDrawing(): void {
        this.drawing = false;

        const canvas = this.canvasRef.nativeElement;
        this.signatureData.emit(canvas.toDataURL());
    }

    private getPosition(event: MouseEvent | TouchEvent): { x: number; y: number } {
        const canvas = this.canvasRef.nativeElement;
        const rect = canvas.getBoundingClientRect();
        const touch = (event as TouchEvent).touches ? (event as TouchEvent).touches[0] : (event as MouseEvent);
        return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
    }

    public clearCanvas(): void {
        if (!this.ctx) return;
        const canvas = this.canvasRef.nativeElement;
        this.ctx.fillStyle = this.backgroundColor;
        this.ctx.fillRect(0, 0, canvas.width, canvas.height);
    }

    @HostListener('mousedown', ['$event'])
    public onMouseDown(event: MouseEvent): void {
        this.startDrawing(event);
    }
    @HostListener('mousemove', ['$event'])
    public onMouseMove(event: MouseEvent): void {
        this.draw(event);
    }
    @HostListener('mouseup')
    public onMouseUp(): void {
        this.stopDrawing();
    }
    @HostListener('mouseleave')
    public onMouseLeave(): void {
        this.stopDrawing();
    }

    @HostListener('touchstart', ['$event'])
    public onTouchStart(event: TouchEvent): void {
        this.startDrawing(event);
    }
    @HostListener('touchmove', ['$event'])
    public onTouchMove(event: TouchEvent): void {
        this.draw(event);
    }
    @HostListener('touchend')
    public onTouchEnd(): void {
        this.stopDrawing();
    }
}
